import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Upload, X, Eye, Search, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ChallengePreview from '@/components/ChallengePreview';
import { uploadFile } from '@/lib/utils';
import RichTextarea from '@/components/RichTextarea';
import { DataPagination } from '@/components/DataPagination';
import { logCreate, logUpdate, logDelete } from '@/lib/audit-log';

interface VideoExample {
  url: string;
  thumbnail_url: string;
  why_it_works: string;
}

const emptyChallenge = {
  day_number: 1, title: '', subtitle: '', description: '', pro_tip: '',
  action_description: '', checklist_items: [] as string[],
  gems_reward: 0, is_active: true, example_videos: [] as VideoExample[],
};

const emptyVideoExample: VideoExample = { url: '', thumbnail_url: '', why_it_works: '' };
type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

// Column configuration with display labels and database field mapping
const COLUMN_CONFIG = [
  { label: 'Day', field: 'day_number', sortable: true },
  { label: 'Title', field: 'title', sortable: true },
  { label: 'Subtitle', field: 'subtitle', sortable: false },
  { label: 'Gems', field: 'gems_reward', sortable: true },
  { label: 'Videos', field: 'example_videos', sortable: false },
  { label: 'Active', field: 'is_active', sortable: false },
  { label: 'Actions', field: 'actions', sortable: false },
];

export default function Challenges() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);
  const [checklistText, setChecklistText] = useState('');
  const [uploading, setUploading] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);

  const uploadThumbnail = async (file: File): Promise<string> => {
    try {
      const ext = file.name.split('.').pop();
      const fileName = `challenge-${Date.now()}.${ext}`;
      return await uploadFile(file, 'challenge-assets', fileName);
    } finally {
      setUploading(null);
    }
  };

  // const { data: challenges } = useQuery({
  //   queryKey: ['challenges', currentPage, pageSize, search, sortConfig?.key, sortConfig?.direction],
  //   queryFn: async () => {
  //     const { data } = await supabase.from('challenges').select('*').order('day_number');
  //     return data ?? [];
  //   },
  // });

   const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'challenges',
      currentPage,
      pageSize,
      search,
      sortConfig?.key,
      sortConfig?.direction,
    ],
    queryFn: async () => {
      let query = supabase
        .from('challenges')
        .select('*', { count: 'exact' });
  
      if (search) {
        query = query.ilike('title', `%${search}%`);
      }
   
      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } 
      else {
        query = query.order('day_number', { ascending: false });
      }
  
      query = query.range(
        (currentPage - 1) * pageSize,
        currentPage * pageSize - 1
      );
  
      const { data, count, error } = await query;
  
      if (error) throw error;
  
      return {
        data: data ?? [],
        total: count ?? 0,
      };
    },
  });

  // Get column config by field name
  const getColumnConfig = (field: string) => {
    return COLUMN_CONFIG.find(col => col.field === field);
  };

  // Handle sort - now uses database field names
  const handleSort = (field: string) => {
    const columnConfig = getColumnConfig(field);
    // Only sort if column is sortable
    if (!columnConfig?.sortable) return;
    
    setCurrentPage(1);
    setSortConfig((current) => {
      if (!current || current.key !== field || current.direction === 'desc') {
        return { key: field, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key: field, direction: 'desc' };
      }
      return null;
    });
  };

  const validateChallenge = (challenge: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Day number validation
    const dayNumber = Number(challenge.day_number);
    if (isNaN(dayNumber) || dayNumber < 1) {
      errors.push('Day Number must be 1 or greater');
    }

    // Gems reward validation
    const gemsReward = Number(challenge.gems_reward);
    if (isNaN(gemsReward) || gemsReward < 0) {
      errors.push('Gems Reward must be 0 or greater');
    }

    // Required text fields
    if (!challenge.title?.trim()) {
      errors.push('Title is required');
    }
    if (!challenge.subtitle?.trim()) {
      errors.push('Subtitle is required');
    }
    if (!challenge.description?.trim()) {
      errors.push('Description is required');
    }

    // Video examples URL validation
    const videoExamples = challenge.example_videos || [];
    videoExamples.forEach((ve: VideoExample, idx: number) => {
  if (ve.url && ve.url.trim()) {
      try {
      let link = ve.url.trim();

      // ✅ Allow URLs without protocol
      if (!/^https?:\/\//i.test(link)) {
        link = 'https://' + link;
      }

      const url = new URL(link);

      // Normalize hostname (remove www)
      const hostname = url.hostname.replace(/^www\./, '');

      // ✅ Strict TikTok domains
      const validHosts = [
        'tiktok.com',
        'm.tiktok.com',
        'vm.tiktok.com',
        'vt.tiktok.com'
      ];

      if (!validHosts.includes(hostname)) {
        throw new Error('Not TikTok domain');
      }

    } catch {
      errors.push(`Video ${idx + 1}: URL must be a valid TikTok link`);
    }
  }
});

    return { valid: errors.length === 0, errors };
  };

  const handleSaveChallenge = () => {
    if (!editing) return;

    // Validate challenge fields
    const validation = validateChallenge(editing);
    if (!validation.valid) {
     toast.error(`Errors:\n• ${validation.errors.join('\n• ')}`);
      return;
    }

    saveMutation.mutate(editing);
  };

  const saveMutation = useMutation({
    mutationFn: async (challenge: any) => {
      const items = checklistText.split('\n').filter(s => s.trim());
      const payload = { ...challenge, checklist_items: items };
      const challengeId = payload.id;
      delete payload.id;
      // Clean up legacy fields if they exist
      delete payload.video_examples;
      delete payload.learn;
      delete payload.why_this_works;
      delete payload.reference_video_url;
      delete payload.reference_video_thumbnail_url;
      
      if (challengeId) {
        // UPDATE - fetch old values for audit log
        const { data: oldData } = await supabase.from('challenges').select('*').eq('id', challengeId).single();
        const { data, error } = await supabase.from('challenges').update(payload).eq('id', challengeId).select().single();
        if (error) throw error;
        await logUpdate('challenges', challengeId, oldData, data, {
          description: `Updated challenge: ${data?.title}`,
          dayNumber: data?.day_number,
        });
      } else {
        // CREATE
        const { data, error } = await supabase.from('challenges').insert(payload).select().single();
        if (error) throw error;
        await logCreate('challenges', data.id, data, {
          description: `Created challenge: ${data?.title}`,
          dayNumber: data?.day_number,
        });
      }
    },
    onSuccess: () => {
      toast.success('Challenge saved');
      setEditing(null);
      setChecklistText('');
      setShowPreview(false);
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Fetch old values for audit log
      const { data: oldData } = await supabase.from('challenges').select('*').eq('id', id).single();
      const { error } = await supabase.from('challenges').delete().eq('id', id);
      if (error) throw error;
      await logDelete('challenges', id, oldData, {
        description: `Deleted challenge: ${oldData?.title || 'Unknown'}`,
        dayNumber: oldData?.day_number,
      });
    },
    onSuccess: () => {
      toast.success('Challenge deleted');
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ id, newDay, swapId, swapDay }: any) => {
      // Fetch old data for audit log
      const { data: challenge1Old } = await supabase.from('challenges').select('*').eq('id', id).single();
      const { data: challenge2Old } = await supabase.from('challenges').select('*').eq('id', swapId).single();
      
      const [result1, result2] = await Promise.all([
        supabase.from('challenges').update({ day_number: newDay }).eq('id', id).select().single(),
        supabase.from('challenges').update({ day_number: swapDay }).eq('id', swapId).select().single(),
      ]);
      
      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;
      
      // Log both reorder actions
      await Promise.all([
        logUpdate('challenges', id, challenge1Old, result1.data, {
          description: `Reordered challenge from day ${swapDay} to day ${newDay}`,
          action: 'reorder',
        }),
        logUpdate('challenges', swapId, challenge2Old, result2.data, {
          description: `Reordered challenge from day ${newDay} to day ${swapDay}`,
          action: 'reorder',
        }),
      ]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['challenges'] }),
  });

  const openEditor = (challenge?: any) => {
    if (challenge) {
      const videoExamples: VideoExample[] = Array.isArray(challenge.example_videos)
        ? challenge.example_videos
        : challenge.reference_video_url
          ? [{ url: challenge.reference_video_url || '', thumbnail_url: challenge.reference_video_thumbnail_url || '', why_it_works: challenge.why_this_works || '' }]
          : [];
      const c = { ...challenge, example_videos: videoExamples };
      setEditing(c);
      setChecklistText(Array.isArray(c.checklist_items) ? c.checklist_items.join('\n') : '');
    } else {
      const c = { ...emptyChallenge, day_number: (data?.data?.length ?? 0) + 1 };
      setEditing(c);
      setChecklistText('');
    }
  };

  const moveChallenge = (index: number, direction: -1 | 1) => {
    if (!data) return;
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= data?.data?.length) return;
    const current = data.data[index];
    const swap = data.data[swapIndex];
    reorderMutation.mutate({
      id: current.id, newDay: swap.day_number,
      swapId: swap.id, swapDay: current.day_number,
    });
  };

  const updateVideoExample = (index: number, field: keyof VideoExample, value: string) => {
    setEditing((prev: any) => {
      const examples = [...(prev.example_videos || [])];
      examples[index] = { ...examples[index], [field]: value };
      return { ...prev, example_videos: examples };
    });
  };

  const addVideoExample = () => {
    setEditing((prev: any) => ({
      ...prev,
      example_videos: [...(prev.example_videos || []), { ...emptyVideoExample }],
    }));
  };

  const removeVideoExample = (index: number) => {
    setEditing((prev: any) => ({
      ...prev,
      example_videos: prev.example_videos.filter((_: any, i: number) => i !== index),
    }));
  };

  const currentChecklist = checklistText.split('\n').filter(s => s.trim());

  const totalPages = Math.ceil(data?.total / pageSize);

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleExportChallenges = async () => {
    setIsExporting(true);
    try {
      let query = supabase
        .from('challenges')
        .select('*');

      if (search) {
        query = query.ilike('title', `%${search}%`);
      }

      if (sortConfig) {
        query = query.order(sortConfig.key, {
          ascending: sortConfig.direction === 'asc',
        });
      } else {
        query = query.order('day_number', { ascending: false });
      }

      const { data: exportData, error } = await query;

      if (error) throw error;
      if (!exportData || exportData.length === 0) {
        toast.error('No challenges available to export');
        return;
      }

      const columns: string[] = Array.from(
        exportData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...exportData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `challenges-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.length} challenges`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export challenges');
    } finally {
      setIsExporting(false);
    }
  };

  // Get sort icon for column - uses database field names
  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.key !== field) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Challenges Manager</h1>
        <Button onClick={() => openEditor()}><Plus className="mr-2 h-4 w-4" /> Add Challenge</Button>
      </div>
      <Card>
        <CardHeader>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="text-xl sm:text-2xl">Current Data ({data?.data.length || 0} rows)</CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="outline" onClick={handleExportChallenges} disabled={isExporting || !data?.data?.length}>
                        <Download className="mr-2 h-4 w-4" />
                        {isExporting ? 'Exporting...' : 'Export'}
                      </Button>
                      <div className="relative w-full sm:w-auto">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search challenges..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          className="pl-8 w-full sm:w-[300px]"
                        />
                      </div>
                    </div>
                  </div>
                </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMN_CONFIG.map((column) => (
                  <TableHead key={column.field} className="whitespace-nowrap">
                    {column.sortable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 data-[state=open]:bg-accent"
                        onClick={() => handleSort(column.field)}
                      >
                        <span>{column.label}</span>
                        {getSortIcon(column.field)}
                      </Button>
                    ) : (
                      // No px-3 — it would offset this label 12px from the cell
                      // text below (the sortable Button cancels its own px-3
                      // with -ml-3, so its label sits flush).
                      <span>{column.label}</span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>

             {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-24 text-center text-muted-foreground"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              ) : (
              data?.data?.map((c: any, i: number) => {
                const videoCount = Array.isArray(c.example_videos) ? c.example_videos.length : (c.reference_video_url ? 1 : 0);
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.day_number}</TableCell>
                    <TableCell>{c.title}</TableCell>
                    <TableCell className="text-muted-foreground">{c.subtitle}</TableCell>
                    <TableCell className="whitespace-nowrap">{c.gems_reward}</TableCell>
                    <TableCell className="whitespace-nowrap">{videoCount}</TableCell>
                    <TableCell>{c.is_active ? '✓' : '✗'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => moveChallenge(i, -1)} disabled={i === 0}>
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => moveChallenge(i, 1)} disabled={i === (data?.data?.length ?? 0) - 1}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditor(c)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete challenge?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteMutation.mutate(c.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }))}
            </TableBody>
          </Table>
          {data?.data?.length > 0 && (
                      <DataPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalItems={data?.total}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={handlePageSizeChange}
                      />
                    )}
        </CardContent>
      </Card>

      <Sheet open={!!editing} onOpenChange={(open) => { if (!open) { setEditing(null); setChecklistText(''); setShowPreview(false); } }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-[1100px]">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>{editing?.id ? 'Edit' : 'New'} Challenge</SheetTitle>
              <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
                <Eye className="mr-1 h-4 w-4" /> {showPreview ? 'Hide' : 'Show'} Preview
              </Button>
            </div>
          </SheetHeader>
          {editing && (
            <div className={`mt-6 ${showPreview ? 'flex gap-8' : ''}`}>
              {/* Form */}
              <div className={`space-y-4 ${showPreview ? 'flex-1 min-w-0' : ''}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Day Number <span className="text-destructive">*</span></Label><Input type="number" min="1" value={editing.day_number} onChange={e => setEditing({ ...editing, day_number: +e.target.value })} /></div>
                  <div><Label>Gems Reward <span className="text-destructive">*</span></Label><Input type="number" min="1" value={editing.gems_reward} onChange={e => setEditing({ ...editing, gems_reward: +e.target.value })} /></div>
                </div>
                <div><Label>Title <span className="text-destructive">*</span></Label><Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label>Subtitle <span className="text-destructive">*</span></Label><Input value={editing.subtitle} onChange={e => setEditing({ ...editing, subtitle: e.target.value })} /></div>
                <div><Label>Description <span className="text-destructive">*</span></Label><RichTextarea value={editing.description || ''} onChange={v => setEditing({ ...editing, description: v })} rows={4} /></div>
                <div><Label>Pro Tip <span className="text-muted-foreground font-normal">(optional)</span></Label><RichTextarea value={editing.pro_tip || ''} onChange={v => setEditing({ ...editing, pro_tip: v })} rows={3} /></div>
                <div><Label>Action Description</Label><RichTextarea value={editing.action_description || ''} onChange={v => setEditing({ ...editing, action_description: v })} rows={3} /></div>
                <div><Label>Checklist Items (one per line)</Label><Textarea value={checklistText} onChange={e => setChecklistText(e.target.value)} rows={4} /></div>

                {/* Video Examples */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Video Examples</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideoExample}>
                      <Plus className="mr-1 h-3 w-3" /> Add Video
                    </Button>
                  </div>
                  {(editing.example_videos || []).map((ve: VideoExample, idx: number) => (
                    <Card key={idx} className="relative">
                      <Button
                        type="button" variant="ghost" size="icon"
                        className="absolute top-2 right-2 h-6 w-6"
                        onClick={() => removeVideoExample(idx)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      <CardContent className="p-3 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Video {idx + 1}</p>
                        <div>
                          <Label className="text-xs">TikTok URL</Label>
                          <Input value={ve.url} onChange={e => updateVideoExample(idx, 'url', e.target.value)} placeholder="https://www.tiktok.com/@username/video/..." />
                          {ve.url && !ve.url.includes('tiktok.com') && (
                            <p className="text-xs text-destructive mt-1">Must be a valid TikTok URL</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Thumbnail</Label>
                          <input
                            ref={el => { fileInputRefs.current[idx] = el; }}
                            type="file" accept="image/*" className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              setUploading(idx);
                              try {
                                const url = await uploadThumbnail(file);
                                updateVideoExample(idx, 'thumbnail_url', url);
                                toast.success('Thumbnail uploaded');
                              } catch (err: any) {
                                toast.error(err.message || 'Upload failed');
                              }
                            }}
                          />
                          <div className="flex items-center gap-2 mt-1">
                            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRefs.current[idx]?.click()} disabled={uploading === idx}>
                              <Upload className="mr-1 h-3 w-3" />{uploading === idx ? 'Uploading...' : 'Upload'}
                            </Button>
                            {ve.thumbnail_url && (
                              <a href={ve.thumbnail_url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground underline truncate max-w-[150px]">View</a>
                            )}
                          </div>
                          {ve.thumbnail_url && (
                            <img src={ve.thumbnail_url} alt="" className="mt-2 rounded-md max-h-28 object-cover" />
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Why It Works</Label>
                          <RichTextarea value={ve.why_it_works} onChange={v => updateVideoExample(idx, 'why_it_works', v)} rows={2} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {(editing.example_videos || []).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-3">No video examples yet. Click "Add Video" above.</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Switch checked={editing.is_active} onCheckedChange={v => setEditing({ ...editing, is_active: v })} />
                  <Label>Active</Label>
                </div>
                <Button className="w-full" onClick={handleSaveChallenge} disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : 'Save Challenge'}
                </Button>
              </div>

              {/* Preview */}
              {showPreview && (
                <div className="shrink-0">
                  <p className="text-xs font-medium text-muted-foreground mb-3 text-center">App Preview</p>
                  <ChallengePreview
                    dayNumber={editing.day_number}
                    title={editing.title}
                    subtitle={editing.subtitle}
                    description={editing.description || ''}
                    proTip={editing.pro_tip}
                    actionDescription={editing.action_description}
                    checklistItems={currentChecklist}
                    gemsReward={editing.gems_reward}
                    videoExamples={editing.example_videos || []}
                  />
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
